import { Fragment, useEffect, useMemo, useState } from "react";
import { GripVertical, Plus, Trash2, Hash, Volume2, Megaphone, Folder, ChevronDown, ChevronRight, Link2, X, MessagesSquare, Radio, FolderInput } from "lucide-react";
import { useStructureCtx } from "@/context/StructureContext";
import { StructureHeader } from "@/components/StructureHeader";
import { CHANNEL_PERM_GROUPS } from "@/lib/structure";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPE_ICON = { text: Hash, announcement: Megaphone, voice: Volume2, stage: Radio, forum: MessagesSquare };
const labelC = "mb-1.5 block text-xs font-bold uppercase tracking-wide text-[#B5BAC1]";
const inputC = "border-[#131620] bg-[#131620] text-white";
const NO_CAT = "__none__";

function cellState(ow, rid, perm) {
  const o = ow[rid] || { allow: [], deny: [] };
  if ((o.allow || []).includes(perm)) return "allow";
  if ((o.deny || []).includes(perm)) return "deny";
  return "inherit";
}

// Matrice permessi (categoria o canale): ruoli in colonna, permessi in riga, 3 stati.
// La colonna dei permessi e l'intestazione dei ruoli restano fisse durante lo scorrimento.
export function PermMatrix({ roles, overwrites, onChange, testid }) {
  const cols = useMemo(() => {
    const ids = Object.keys(overwrites);
    const order = (id) => { const i = roles.findIndex((r) => (r.id || r.uid) === id); return i === -1 ? 999 : i; };
    return ids.map((id) => roles.find((r) => (r.id || r.uid) === id) || { id, name: id, color: "#949BA4" })
      .sort((a, b) => order(a.id || a.uid) - order(b.id || b.uid));
  }, [overwrites, roles]);

  const notInMatrix = roles.filter((r) => !overwrites[r.id || r.uid]);

  const cycle = (rid, perm) => {
    const o = { allow: [...(overwrites[rid]?.allow || [])], deny: [...(overwrites[rid]?.deny || [])] };
    const st = cellState(overwrites, rid, perm);
    o.allow = o.allow.filter((x) => x !== perm); o.deny = o.deny.filter((x) => x !== perm);
    if (st === "inherit") o.allow.push(perm); else if (st === "allow") o.deny.push(perm);
    onChange({ ...overwrites, [rid]: o });
  };
  const addCol = (rid) => onChange({ ...overwrites, [rid]: { allow: [], deny: [] } });
  const removeCol = (rid) => { const n = { ...overwrites }; delete n[rid]; onChange(n); };

  return (
    <div data-testid={testid}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-4 text-xs text-[#B5BAC1]">
          <span className="flex items-center gap-1.5"><span className="flex h-4 w-4 items-center justify-center rounded bg-[#23A55A] text-[10px] text-white">✓</span> Consenti</span>
          <span className="flex items-center gap-1.5"><span className="flex h-4 w-4 items-center justify-center rounded bg-[#F23F43] text-[10px] text-white">✕</span> Nega</span>
          <span className="flex items-center gap-1.5"><span className="flex h-4 w-4 items-center justify-center rounded bg-[#131620] text-[10px] text-[#4b4d54]">/</span> Come da ruolo</span>
        </div>
        <Select value="" onValueChange={addCol}>
          <SelectTrigger data-testid="matrix-add-role" className="h-9 w-auto gap-1.5 border-[#5865F2]/50 bg-[#5865F2]/15 px-3 text-xs font-semibold text-[#a9b2ff] hover:bg-[#5865F2]/25 [&>svg]:hidden">
            <Plus className="h-4 w-4" /><span>Aggiungi ruolo ai permessi</span>
          </SelectTrigger>
          <SelectContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
            {notInMatrix.length === 0 && <div className="px-2 py-1.5 text-xs text-[#949BA4]">Tutti i ruoli già presenti</div>}
            {notInMatrix.map((r) => <SelectItem key={r.id || r.uid} value={r.id || r.uid} className="focus:bg-[#252B3D]"><span className="mr-2 inline-block h-2 w-2 rounded-full align-middle" style={{ background: r.color }} />{r.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="max-h-[65vh] overflow-auto rounded-lg border border-[#232838]">
        <table className="w-full border-separate border-spacing-0 text-xs">
          <thead>
            <tr>
              <th className="sticky left-0 top-0 z-30 min-w-[180px] border-b border-r border-[#232838] bg-[#131620] p-2 text-left font-semibold text-[#949BA4] sm:min-w-[220px]">Permesso</th>
              {cols.map((r) => (
                <th key={r.id || r.uid} className="sticky top-0 z-20 min-w-[90px] border-b border-[#232838] bg-[#131620] p-2 text-center">
                  <div className="flex items-center justify-center gap-1 whitespace-nowrap">
                    <span className="h-2 w-2 rounded-full" style={{ background: r.color }} />
                    <span className="max-w-[80px] truncate font-medium text-white">{r.name}</span>
                    <button onClick={() => removeCol(r.id || r.uid)} title="Togli dai permessi" data-testid={`matrix-remove-col-${r.id || r.uid}`} className="text-[#4b4d54] hover:text-[#F23F43]"><X className="h-3 w-3" /></button>
                  </div>
                </th>
              ))}
              {cols.length === 0 && <th className="sticky top-0 border-b border-[#232838] bg-[#131620] p-2 text-left text-[11px] font-normal text-[#4b4d54]">Aggiungi un ruolo per iniziare →</th>}
            </tr>
          </thead>
          <tbody>
            {CHANNEL_PERM_GROUPS.map((g) => (
              <Fragment key={g.category}>
                <tr><td colSpan={cols.length + 1} className="bg-[#0f111a] p-0"><span className="sticky left-0 block w-fit px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-[#4b4d54]">{g.category}</span></td></tr>
                {g.items.map((p) => (
                  <tr key={p.value} className="group">
                    <td className="sticky left-0 z-10 border-r border-t border-[#232838] bg-[#161923] p-2 text-[#DBDEE1] group-hover:bg-[#1b1f2e]">{p.label}</td>
                    {cols.map((r) => {
                      const rid = r.id || r.uid;
                      const st = cellState(overwrites, rid, p.value);
                      const c = st === "allow" ? "bg-[#23A55A] text-white" : st === "deny" ? "bg-[#F23F43] text-white" : "bg-[#131620] text-[#4b4d54] hover:bg-[#252B3D]";
                      const sym = st === "allow" ? "✓" : st === "deny" ? "✕" : "/";
                      return <td key={rid} className="border-t border-[#232838] p-1 text-center group-hover:bg-[#131620]/40"><button onClick={() => cycle(rid, p.value)} data-testid={`matrix-cell-${rid}-${p.value}`} className={`h-7 w-7 rounded ${c} transition-colors`}>{sym}</button></td>;
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Channels() {
  const s = useStructureCtx();
  const { categories, loose, roles } = s;
  const [sel, setSel] = useState(null); // {catUid, chUid}; chUid null = categoria selezionata
  const [drag, setDrag] = useState(null); // {kind:"ch", catUid, uid} | {kind:"cat", uid}
  const [over, setOver] = useState(null); // chiave del punto di rilascio evidenziato
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => {
    const valid = sel && (sel.chUid
      ? (sel.catUid ? categories.find((c) => c.uid === sel.catUid)?.channels : loose)?.some((c) => c.uid === sel.chUid)
      : categories.some((c) => c.uid === sel.catUid));
    if (valid) return;
    if (categories[0]) setSel({ catUid: categories[0].uid, chUid: null });
    else if (loose[0]) setSel({ catUid: null, chUid: loose[0].uid });
  }, [categories, loose, sel]);

  const findCat = (u) => categories.find((c) => c.uid === u);
  const isCatSel = sel && sel.catUid && !sel.chUid;
  const selCat = sel?.catUid ? findCat(sel.catUid) : null;
  const selCh = sel?.chUid ? (sel.catUid ? selCat?.channels.find((c) => c.uid === sel.chUid) : loose.find((c) => c.uid === sel.chUid)) : null;

  // "+ Canale" crea dentro la categoria selezionata (o quella del canale selezionato).
  const newChannel = () => {
    const target = sel?.catUid || null;
    const u = s.addChannel(target);
    if (target) setCollapsed((c) => ({ ...c, [target]: false }));
    setSel({ catUid: target, chUid: u });
  };
  const newCategory = () => { const u = s.addCategory(); setSel({ catUid: u, chUid: null }); };

  // ── drag & drop ──
  const endDrag = () => { setDrag(null); setOver(null); };
  const dropOnChannel = (catUid, beforeUid) => {
    if (drag?.kind === "ch") {
      s.moveChannel(drag.catUid, drag.uid, catUid, beforeUid);
      if (sel?.chUid === drag.uid) setSel({ catUid, chUid: drag.uid });
    }
    endDrag();
  };
  const dropOnCategory = (catUid) => {
    if (drag?.kind === "ch") {
      s.moveChannel(drag.catUid, drag.uid, catUid, null);
      setCollapsed((c) => ({ ...c, [catUid]: false }));
      if (sel?.chUid === drag.uid) setSel({ catUid, chUid: drag.uid });
    } else if (drag?.kind === "cat") s.moveCategory(drag.uid, catUid);
    endDrag();
  };
  const dz = (key, onDrop) => ({
    onDragOver: (e) => { if (!drag) return; e.preventDefault(); e.stopPropagation(); if (over !== key) setOver(key); },
    onDrop: (e) => { e.preventDefault(); e.stopPropagation(); onDrop(); },
  });

  const rowClass = (active) => `group flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer select-none transition-colors ${active ? "bg-[#5865F2]/20 ring-1 ring-[#5865F2]/40" : "hover:bg-[#1b1f2e]"}`;
  const dropLine = (key) => over === key ? "shadow-[inset_0_2px_0_#5865F2]" : "";

  const channelRow = (ch, catUid) => {
    const Icon = TYPE_ICON[ch.type] || Hash;
    const active = sel?.chUid === ch.uid;
    const key = `ch:${ch.uid}`;
    return (
      <div draggable onDragStart={(e) => { e.stopPropagation(); e.dataTransfer.effectAllowed = "move"; setDrag({ kind: "ch", catUid, uid: ch.uid }); }} onDragEnd={endDrag}
        {...dz(key, () => dropOnChannel(catUid, ch.uid))}
        className={`rounded-md ${dropLine(key)} ${drag?.uid === ch.uid ? "opacity-40" : ""}`}>
        <div className={rowClass(active)} onClick={() => setSel({ catUid, chUid: ch.uid })} data-testid={`channel-node-${s.slug(ch.name)}`}>
          <GripVertical className="h-3.5 w-3.5 shrink-0 cursor-grab text-[#4b4d54] group-hover:text-[#949BA4]" />
          <Icon className="h-4 w-4 shrink-0 text-[#949BA4]" />
          <span className="flex-1 truncate text-sm text-[#DBDEE1]">{ch.name}</span>
          {ch.syncWithCategory && <Link2 className="h-3 w-3 text-[#4b4d54]" title="Permessi sincronizzati con la categoria" />}
          <button onClick={(e) => { e.stopPropagation(); s.delChannel(catUid, ch.uid); if (active) setSel(null); }} title="Elimina canale" className="text-[#4b4d54] opacity-0 hover:text-[#F23F43] group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5 pb-20">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-white sm:text-3xl">Canali &amp; categorie</h1>
        <p className="text-sm text-slate-400">Trascina i canali per spostarli dentro una categoria o riordinarli. Le modifiche vanno su Discord quando premi «Salva».</p>
      </div>
      <StructureHeader s={s} tab="canali" />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[320px_minmax(0,1fr)]">
        {/* ── albero ── */}
        <Card className="min-w-0 border-[#232838] bg-[#161923] p-3">
          <div className="mb-3 flex gap-2">
            <Button size="sm" variant="outline" onClick={newCategory} data-testid="add-category-btn" className="flex-1 border-[#232838] bg-transparent text-[#B5BAC1] hover:bg-[#252B3D] hover:text-white"><Folder className="mr-1 h-4 w-4" />Categoria</Button>
            <Button size="sm" onClick={newChannel} data-testid="add-loose-channel-btn" className="flex-1 bg-[#5865F2] hover:bg-[#4752C4]"
              title={selCat ? `Crea un canale in ${selCat.name}` : "Crea un canale fuori dalle categorie"}><Plus className="mr-1 h-4 w-4" />Canale</Button>
          </div>
          {selCat && <p className="mb-2 px-1 text-[11px] text-[#949BA4]">Il nuovo canale verrà creato in <b className="text-[#B5BAC1]">{selCat.name}</b>.</p>}

          <div className="max-h-[70vh] space-y-2 overflow-y-auto pr-1">
            {/* canali senza categoria (in cima, come su Discord) */}
            <div {...dz("loose", () => dropOnChannel(null, null))}
              className={`rounded-lg border border-dashed p-1 transition-colors ${over === "loose" ? "border-[#5865F2] bg-[#5865F2]/10" : loose.length || drag?.kind === "ch" ? "border-[#232838]" : "hidden"}`}>
              <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-[#4b4d54]">Senza categoria</p>
              {loose.map((ch) => <Fragment key={ch.uid}>{channelRow(ch, null)}</Fragment>)}
              {loose.length === 0 && <p className="px-2 pb-1 text-[11px] text-[#949BA4]">Rilascia qui per toglierlo dalla categoria</p>}
            </div>

            {categories.map((cat) => {
              const isOpen = !collapsed[cat.uid];
              const key = `cat:${cat.uid}`;
              return (
                <div key={cat.uid} {...dz(key, () => dropOnCategory(cat.uid))}
                  className={`rounded-lg p-1.5 transition-colors ${over === key ? "bg-[#5865F2]/10 ring-1 ring-[#5865F2]/60" : "bg-[#12141d]"} ${drag?.uid === cat.uid ? "opacity-40" : ""}`}
                  data-testid={`category-node-${s.slug(cat.name)}`}>
                  <div draggable onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; setDrag({ kind: "cat", uid: cat.uid }); }} onDragEnd={endDrag}
                    className={rowClass(isCatSel && sel.catUid === cat.uid)} onClick={() => setSel({ catUid: cat.uid, chUid: null })}>
                    <GripVertical className="h-3.5 w-3.5 shrink-0 cursor-grab text-[#4b4d54] group-hover:text-[#949BA4]" />
                    <button onClick={(e) => { e.stopPropagation(); setCollapsed((c) => ({ ...c, [cat.uid]: isOpen })); }} className="text-[#949BA4] hover:text-white">
                      {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    </button>
                    <span className="flex-1 truncate text-xs font-bold uppercase tracking-wide text-[#B5BAC1]">{cat.name}</span>
                    <span className="text-[10px] text-[#4b4d54]">{cat.channels.length}</span>
                    <button onClick={(e) => { e.stopPropagation(); const u = s.addChannel(cat.uid); setCollapsed((c) => ({ ...c, [cat.uid]: false })); setSel({ catUid: cat.uid, chUid: u }); }} title="Nuovo canale in questa categoria" data-testid={`cat-add-channel-${s.slug(cat.name)}`} className="text-[#949BA4] hover:text-white"><Plus className="h-4 w-4" /></button>
                    <button onClick={(e) => { e.stopPropagation(); s.delCategory(cat.uid); if (sel?.catUid === cat.uid) setSel(null); }} title="Elimina categoria e i suoi canali" className="text-[#4b4d54] opacity-0 hover:text-[#F23F43] group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                  {isOpen && (
                    <div className="space-y-0.5 pl-4 pt-0.5">
                      {cat.channels.map((ch) => <Fragment key={ch.uid}>{channelRow(ch, cat.uid)}</Fragment>)}
                      {cat.channels.length === 0 && <p className="px-2 py-1 text-[11px] text-[#4b4d54]">{drag?.kind === "ch" ? "Rilascia qui" : "Nessun canale"}</p>}
                    </div>
                  )}
                </div>
              );
            })}
            {categories.length === 0 && loose.length === 0 && <p className="p-4 text-center text-sm text-[#4b4d54]">Nessun canale. Aggiungi una categoria o un canale.</p>}
          </div>
        </Card>

        {/* ── dettaglio + matrice ── */}
        <Card className="min-w-0 border-[#232838] bg-[#161923] p-5">
          {!selCat && !selCh ? (
            <div className="flex h-full min-h-[320px] flex-col items-center justify-center gap-3 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#5865F2]/15 shadow-[0_0_30px_-8px_#5865F2]"><Hash className="h-6 w-6 text-[#8ea0ff]" /></div>
              <div className="text-base font-bold text-white">Nessun elemento selezionato</div>
              <p className="max-w-xs text-sm text-[#949BA4]">Clicca una categoria o un canale nella lista per modificarne nome, argomento e permessi.</p>
            </div>
          ) : isCatSel && selCat ? (
            <div className="space-y-5" data-testid="category-detail">
              <div>
                <label className={labelC}>Nome della categoria</label>
                <Input value={selCat.name} onChange={(e) => s.updCategory(selCat.uid, { name: e.target.value })} data-testid="category-name-input" className={inputC} />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">Permessi della categoria</h3>
                <p className="mb-3 text-xs text-[#949BA4]">Valgono per tutti i canali della categoria che hanno «Sincronizza con la categoria» attivo.</p>
                <PermMatrix testid="category-perm-matrix" roles={roles} overwrites={selCat.overwrites} onChange={(ow) => s.updCategory(selCat.uid, { overwrites: ow })} />
              </div>
            </div>
          ) : selCh ? (
            <div className="space-y-5" data-testid="channel-detail">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div><label className={labelC}>Nome del canale</label><Input value={selCh.name} onChange={(e) => s.updChannel(sel.catUid, selCh.uid, { name: e.target.value })} data-testid="channel-name-input" className={inputC} /></div>
                <div><label className={labelC}>Categoria</label>
                  <Select value={sel.catUid || NO_CAT} onValueChange={(v) => { const to = v === NO_CAT ? null : v; s.moveChannel(sel.catUid, selCh.uid, to); setSel({ catUid: to, chUid: selCh.uid }); if (to) setCollapsed((c) => ({ ...c, [to]: false })); }}>
                    <SelectTrigger className={inputC} data-testid="channel-category-select"><FolderInput className="mr-2 h-4 w-4 text-[#949BA4]" /><SelectValue /></SelectTrigger>
                    <SelectContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
                      <SelectItem value={NO_CAT} className="focus:bg-[#252B3D]">— Nessuna categoria —</SelectItem>
                      {categories.map((c) => <SelectItem key={c.uid} value={c.uid} className="focus:bg-[#252B3D]">{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div><label className={labelC}>Tipo</label>
                  <Select value={selCh.type} onValueChange={(v) => s.updChannel(sel.catUid, selCh.uid, { type: v })}>
                    <SelectTrigger className={inputC} data-testid="channel-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
                      {(s.meta?.channelTypes || []).filter((t) => t.value !== "category").map((t) => <SelectItem key={t.value} value={t.value} className="focus:bg-[#252B3D]">{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2"><label className={labelC}>Argomento</label><Input value={selCh.topic} placeholder="Descrizione mostrata in alto nel canale" onChange={(e) => s.updChannel(sel.catUid, selCh.uid, { topic: e.target.value })} className={inputC} /></div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div><label className={labelC}>Slowmode (secondi tra un messaggio e l'altro)</label><Input type="number" min={0} value={selCh.slowmode} onChange={(e) => s.updChannel(sel.catUid, selCh.uid, { slowmode: Number(e.target.value) })} className={inputC} /></div>
                {(selCh.type === "voice" || selCh.type === "stage") && (
                  <div><label className={labelC}>Limite utenti (0 = nessuno)</label><Input type="number" min={0} max={99} value={selCh.userLimit} onChange={(e) => s.updChannel(sel.catUid, selCh.uid, { userLimit: Number(e.target.value) })} className={inputC} /></div>
                )}
              </div>
              {sel.catUid && (
                <label className="flex items-center gap-2 text-sm text-[#B5BAC1]"><Switch checked={selCh.syncWithCategory} onCheckedChange={(v) => s.updChannel(sel.catUid, selCh.uid, { syncWithCategory: v })} data-testid="channel-sync-toggle" />Sincronizza permessi con la categoria</label>
              )}
              {!selCh.syncWithCategory ? (
                <div>
                  <h3 className="text-base font-bold text-white">Permessi del canale</h3>
                  <p className="mb-3 text-xs text-[#949BA4]">Decidi chi può vedere, scrivere o parlare in questo canale.</p>
                  <PermMatrix testid="channel-perm-matrix" roles={roles} overwrites={selCh.overwrites} onChange={(ow) => s.updChannel(sel.catUid, selCh.uid, { overwrites: ow })} />
                </div>
              ) : (
                <p className="rounded-lg border border-[#232838] bg-[#131620] p-3 text-sm text-[#949BA4]">Questo canale usa gli stessi permessi della categoria. Disattiva la sincronizzazione per personalizzarli.</p>
              )}
            </div>
          ) : null}
        </Card>
      </div>
    </div>
  );
}
